// Admin v2 — Klubok (Figma ADM_04, 04b, 04c): statkártyák, ellenőrzési kérelmek, lenyíló klublista,
// új klub felvétele tulajdonos-választóval. Séma: a mobil-onboarding clubs/{ownerUid} dokumentuma.
import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, DocumentData, getDocs, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, UpdateData } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtDate, fmtInt, toDate } from '../data';
import {
  Avatar, Badge, Button, ConfirmModal, Empty, Field, Input, Modal, PageHeader, SectionTitle, Select, Spinner, StatCard, TextArea, useToast,
} from '../ui';

// A mobil-onboarding (ClubOnboarding.tsx) értékeivel azonos listák
const CLUB_TYPES = ['Darts klub', 'Egyesület', 'Pub / bar', 'Egyéb'];
const BOARD_TYPES = ['Steel tip', 'Soft tip', 'Mindkettő'];
const COUNTRIES = ['Magyarország', 'Ausztria', 'Szlovákia', 'Románia', 'Szerbia', 'Horvátország', 'Szlovénia', 'Ukrajna', 'Egyéb'];

interface Club {
  id: string;
  ownerUid: string;
  name: string;
  shortName: string;
  country: string;
  city: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  clubType: string;
  boardType: string;
  boardCount: number;
  description: string;
  logo: string | null;
  verified: boolean;
  requested: boolean;
  requestedAt: Date | null;
}

interface Person { uid: string; name: string; email: string; avatar: string | null }

const s = (v: unknown) => (typeof v === 'string' ? v : '');

export default function ClubsPage() {
  const toast = useToast();
  const [clubs, setClubs] = useState<Club[] | null>(null);
  const [bookings, setBookings] = useState<Record<string, number>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [modal, setModal] = useState<Club | 'new' | null>(null);
  const [revoke, setRevoke] = useState<Club | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => onSnapshot(collection(db, 'clubs'), (snap) => {
    setClubs(snap.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id, ownerUid: s(v.ownerUid) || d.id, name: s(v.name) || 'Névtelen klub', shortName: s(v.shortName),
        country: s(v.country) || 'Magyarország', city: s(v.city), address: s(v.address), email: s(v.email), phone: s(v.phone),
        website: s(v.website), clubType: s(v.clubType), boardType: s(v.boardType), boardCount: Number(v.boardCount ?? 0),
        description: s(v.description), logo: s(v.logoUrl) || s(v.photoURL) || s(v.avatarUrl) || null,
        verified: v.verified === true, requested: v.verificationRequested === true, requestedAt: toDate(v.verificationRequestedAt),
      };
    }).sort((a, b) => a.name.localeCompare(b.name, 'hu')));
  }, () => setClubs([])), []);

  useEffect(() => {
    getDocs(collection(db, 'bookings')).then((sn) => {
      const m: Record<string, number> = {};
      sn.docs.forEach((d) => { const c = d.data().clubId; if (c) m[c] = (m[c] ?? 0) + 1; });
      setBookings(m);
    }).catch(() => undefined);
    getDocs(query(collection(db, 'users'), limit(1000))).then((sn) => setPeople(sn.docs.map((d) => {
      const v = d.data();
      return { uid: d.id, name: s(v.displayName) || s(v.username) || s(v.email) || d.id, email: s(v.email), avatar: s(v.avatarUrl) || s(v.photoURL) || null };
    }))).catch(() => undefined);
  }, []);

  const byUid = useMemo(() => new Map(people.map((p) => [p.uid, p])), [people]);
  const all = clubs ?? [];
  const pending = all.filter((c) => c.requested && !c.verified);
  const list = all.filter((c) => {
    const t = q.trim().toLowerCase();
    return !t || c.name.toLowerCase().includes(t) || c.city.toLowerCase().includes(t);
  });

  const act = async (c: Club, patch: UpdateData<DocumentData>, ok: string) => {
    setBusy(c.id);
    try { await updateDoc(doc(db, 'clubs', c.id), patch); toast(ok); } catch { toast('A művelet nem sikerült.', 'red'); }
    setBusy(null);
  };

  const sub = (c: Club) => [c.city || '—', `${fmtInt(c.boardCount)} tábla`, `${fmtInt(bookings[c.id] ?? 0)} foglalás`].join(' · ');

  return (
    <div>
      <PageHeader
        title="Klubok"
        sub="Dart klubok kezelése és hitelesítése"
        right={<Button icon="Plus" onClick={() => setModal('new')}>Új klub</Button>}
      />

      {clubs === null ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon="Buildings" tone="cyan" label="Összes klub" value={fmtInt(all.length)} />
            <StatCard icon="CheckCircle" tone="green" label="Hitelesített" value={fmtInt(all.filter((c) => c.verified).length)} valueColor="#2bc48a" />
            <StatCard icon="Clock" tone="gold" label="Függő kérelem" value={fmtInt(pending.length)} valueColor="#d9a514" />
          </div>

          {pending.length > 0 && (
            <div className="mt-6 p-5 rounded-2xl border border-[#d9a514]/60" style={{ background: 'rgba(217,165,20,0.04)' }}>
              <SectionTitle right={<Badge tone="gold">{pending.length} függő</Badge>}>Ellenőrzési kérelmek</SectionTitle>
              <div className="flex flex-col gap-2.5">
                {pending.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#1d232a] border border-[#31383D] flex-wrap">
                    <Avatar src={c.logo} name={c.name} size={34} />
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[14px] font-semibold text-white">{c.name}</div>
                      <div className="text-[12px] text-[#8a8f99]">
                        {[c.city || '—', `${fmtInt(c.boardCount)} tábla`, c.requestedAt ? `kérelem: ${fmtDate(c.requestedAt)}` : ''].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <button type="button" disabled={busy === c.id}
                      onClick={() => act(c, { verificationRequested: false }, 'Kérelem elutasítva.')}
                      className="h-[32px] px-3.5 rounded-full border border-[#ef4444] text-[#ef4444] text-[13px] font-semibold hover:bg-[#ef4444]/10 disabled:opacity-50">
                      Elutasítás
                    </button>
                    <button type="button" disabled={busy === c.id}
                      onClick={() => act(c, { verified: true, verificationRequested: false }, 'Klub hitelesítve.')}
                      className="inline-flex items-center gap-1.5 h-[32px] px-3.5 rounded-full bg-[#2bc48a] text-white text-[13px] font-semibold hover:brightness-110 disabled:opacity-50">
                      <Icon name="CheckCircle" size={16} />Hitelesítés
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 mb-4 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-[15px] font-extrabold italic uppercase text-white">Klublista</h2>
            <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név vagy város szerint..." className="w-full sm:w-[316px]" />
          </div>

          <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
            {list.length === 0 && <Empty>{all.length ? 'Nincs a keresésnek megfelelő klub.' : 'Még nincs klub.'}</Empty>}
            {list.map((c, i) => {
              const isOpen = open === c.id;
              const owner = byUid.get(c.ownerUid);
              return (
                <div key={c.id} className={i ? 'border-t border-[#31383D]' : ''}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : c.id)}
                    className="w-full flex items-center gap-3 px-4 h-[72px] text-left hover:bg-white/[0.02] transition" aria-expanded={isOpen}>
                    <Avatar src={c.logo} name={c.name} size={34} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14px] font-semibold text-white truncate">{c.name}</span>
                      <span className="block text-[12px] text-[#8a8f99] truncate">{sub(c)}</span>
                    </span>
                    {c.verified ? <Badge tone="green">Hitelesítve</Badge> : c.requested ? <Badge tone="gold">Függő kérelem</Badge> : <Badge tone="gray">Nem hitelesített</Badge>}
                    <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size={16} className="text-[#8a8f99] ml-2" />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
                        <dl>
                          <Row k="Leírás" v={c.description} />
                          <Row k="Ország" v={c.country} />
                          <Row k="Táblatípus" v={c.boardType} />
                          <Row k="Klubtípus" v={c.clubType} />
                          <Row k="Cím" v={[c.city, c.address].filter(Boolean).join(', ')} />
                        </dl>
                        <dl>
                          <Row k="Foglalások száma" v={fmtInt(bookings[c.id] ?? 0)} />
                          <Row k="Tulajdonos" v={owner?.name ?? ''} />
                          <Row k="Email" v={c.email} />
                          <Row k="Telefon" v={c.phone} />
                          <Row k="Weboldal" v={c.website} />
                        </dl>
                      </div>
                      <div className="flex justify-end gap-3 mt-4">
                        <Button kind="ghost" icon="PencilSimpleLine" onClick={() => setModal(c)}>Szerkesztés</Button>
                        {c.verified ? (
                          <button type="button" onClick={() => setRevoke(c)}
                            className="h-[37px] px-4 rounded-full border border-[#ef4444] text-[#ef4444] text-[14px] font-semibold hover:bg-[#ef4444]/10">
                            Hitelesítés visszavonása
                          </button>
                        ) : (
                          <Button icon="CheckCircle" disabled={busy === c.id} onClick={() => act(c, { verified: true, verificationRequested: false }, 'Klub hitelesítve.')}>
                            Hitelesítés
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {modal && <ClubModal club={modal === 'new' ? null : modal} people={people} onClose={() => setModal(null)} />}
      {revoke && (
        <ConfirmModal
          title="Hitelesítés visszavonása"
          text={<>A(z) <b className="text-white">{revoke.name}</b> klub elveszíti a hitelesített jelvényt.</>}
          confirmLabel="Visszavonás"
          danger
          onClose={() => setRevoke(null)}
          onConfirm={async () => { await act(revoke, { verified: false }, 'Hitelesítés visszavonva.'); setRevoke(null); }}
        />
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-[7px]">
      <dt className="text-[13px] text-[#8a8f99] shrink-0">{k}</dt>
      <dd className="text-[13px] text-white text-right break-words min-w-0">{v || '—'}</dd>
    </div>
  );
}

/* ---------- Új klub felvétele / szerkesztés (ADM_04c) ---------- */
function ClubModal({ club, people, onClose }: { club: Club | null; people: Person[]; onClose: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({
    name: club?.name ?? '', shortName: club?.shortName ?? '', city: club?.city ?? '', address: club?.address ?? '',
    clubType: club?.clubType || CLUB_TYPES[0], boardType: club?.boardType || BOARD_TYPES[0],
    boardCount: String(club?.boardCount || 4), country: club?.country || 'Magyarország',
    email: club?.email ?? '', phone: club?.phone ?? '', website: club?.website ?? '', description: club?.description ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const [ownerQ, setOwnerQ] = useState('');
  const [owner, setOwner] = useState<Person | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const matches = useMemo(() => {
    const t = ownerQ.trim().toLowerCase();
    if (t.length < 2) return [];
    return people.filter((p) => p.name.toLowerCase().includes(t) || p.email.toLowerCase().includes(t)).slice(0, 6);
  }, [ownerQ, people]);

  const opt = (arr: string[], cur: string) => (arr.includes(cur) ? arr : [cur, ...arr]).map((x) => ({ value: x, label: x }));

  const save = async () => {
    setMsg('');
    if (!f.name.trim() || !f.city.trim()) return setMsg('A klub neve és a város megadása kötelező.');
    setBusy(true);
    const data = {
      name: f.name.trim(), shortName: f.shortName.trim(), country: f.country, city: f.city.trim(), address: f.address.trim(),
      email: f.email.trim(), phone: f.phone.trim(), website: f.website.trim(), clubType: f.clubType,
      description: f.description.trim(), boardType: f.boardType, boardCount: Math.max(1, Math.round(Number(f.boardCount) || 1)),
    };
    try {
      if (club) {
        await updateDoc(doc(db, 'clubs', club.id), data);
        toast('Klub mentve.');
      } else {
        const full = {
          ...data, ownerUid: owner?.uid ?? '', services: [] as string[], verified: true, verificationRequested: false,
          createdByAdmin: true, createdAt: serverTimestamp(),
        };
        // A mobil-flow sémája: clubs/{ownerUid}
        if (owner) await setDoc(doc(db, 'clubs', owner.uid), full);
        else await addDoc(collection(db, 'clubs'), full);
        toast('Klub létrehozva.');
      }
      onClose();
    } catch (e) {
      console.warn('[clubs] mentés', e);
      setMsg('A mentés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={club ? 'Klub szerkesztése' : 'Új klub felvétele'}
      onClose={onClose}
      width={566}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button icon={club ? undefined : 'Plus'} onClick={save} disabled={busy}>{club ? 'Mentés' : 'Klub létrehozása'}</Button></>}
    >
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Klub neve"><Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Pl. Bulls Eye Darts Club" /></Field>
        <Field label="Rövid név"><Input value={f.shortName} onChange={(e) => set('shortName', e.target.value)} placeholder="Pl. BEDC" /></Field>
        <Field label="Város"><Input value={f.city} onChange={(e) => set('city', e.target.value)} placeholder="Pl. Budapest" /></Field>
        <Field label="Cím"><Input value={f.address} onChange={(e) => set('address', e.target.value)} placeholder="Utca, házszám" /></Field>
        <Field label="Klubtípus"><Select value={f.clubType} onChange={(v) => set('clubType', v)} options={opt(CLUB_TYPES, f.clubType)} /></Field>
        <Field label="Táblatípus"><Select value={f.boardType} onChange={(v) => set('boardType', v)} options={opt(BOARD_TYPES, f.boardType)} /></Field>
        <Field label="Táblák száma"><Input inputMode="numeric" value={f.boardCount} onChange={(e) => set('boardCount', e.target.value.replace(/[^\d]/g, ''))} /></Field>
        <Field label="Ország"><Select value={f.country} onChange={(v) => set('country', v)} options={opt(COUNTRIES, f.country)} /></Field>
        <Field label="Email"><Input icon="Envelope" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="klub@example.com" /></Field>
        <Field label="Telefon"><Input icon="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+36 ..." /></Field>
        <Field label="Weboldal" className="col-span-2"><Input icon="GlobeSimple" value={f.website} onChange={(e) => set('website', e.target.value)} placeholder="https://..." /></Field>
        <Field label="Leírás" className="col-span-2"><TextArea rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Mutasd be a klubot pár mondatban..." /></Field>
        {!club && (
          <div className="col-span-2">
            <Field label="Tulajdonos (opcionális)">
              <Input icon="MagnifyingGlass" value={ownerQ} onChange={(e) => setOwnerQ(e.target.value)} placeholder="Tulajdonos keresése (név / email)..." />
            </Field>
            {matches.length > 0 && !owner && (
              <div className="mt-2 rounded-xl border border-[#31383D] bg-[#1d232a] overflow-hidden">
                {matches.map((p) => (
                  <button key={p.uid} type="button" onClick={() => { setOwner(p); setOwnerQ(''); }}
                    className="w-full flex items-center gap-2.5 px-3 h-[40px] text-left text-[13px] text-white hover:bg-white/5">
                    <Avatar src={p.avatar} name={p.name} size={24} />
                    <span className="truncate">{p.name}</span>
                    <span className="text-[#8a8f99] truncate">{p.email}</span>
                  </button>
                ))}
              </div>
            )}
            {owner && (
              <span className="mt-3 inline-flex items-center gap-2 h-[32px] pl-1.5 pr-2.5 rounded-full bg-[#00AFF2]/[0.14] border border-[#00AFF2]/40 text-[13px] text-white">
                <Avatar src={owner.avatar} name={owner.name} size={22} />
                {owner.name}{owner.email ? ` · ${owner.email}` : ''}
                <button type="button" onClick={() => setOwner(null)} aria-label="Tulajdonos törlése" className="text-[#B8B8C3] hover:text-white"><Icon name="X" size={14} /></button>
              </span>
            )}
            <div className="mt-3 flex items-center gap-2 text-[12px] text-[#2bc48a]">
              <Icon name="CheckCircle" size={16} />Az admin által felvett klub automatikusan hitelesített.
            </div>
          </div>
        )}
      </div>
      {msg && <div className="mt-4 text-[13px] text-[#ef4444]" role="alert">{msg}</div>}
    </Modal>
  );
}
